import { getFirebase } from 'firestorter';

/** Thrown when the category moved to another offering while this assignment was being made. */
const CATEGORY_REASSIGNED = 'CATEGORY_REASSIGNED';

/**
 * Assign an Inside Sales category to an offering, clearing it from whichever offering held it before.
 *
 * An offering is either a Package or a challenge UserContract, and a category holds exactly one of
 * them per coach. The clear and the set run in one transaction so the two documents are never both
 * carrying the category, which would make the lead modal show two offerings for it.
 *
 * The transaction also re-reads the outgoing holder and gives up when it no longer carries the
 * category, which is what a second admin reassigning it at the same time looks like. That closes
 * the race between two admins who both moved the same assigned category; two admins assigning a
 * category that was *unassigned* can still both win, because Firestore cannot read "whoever holds
 * this category" inside a transaction. Closing that last gap needs the assignment to live in one
 * document per coach and category, or to be serialized server-side.
 *
 * @param {object} params The assignment.
 * @param {string} params.category The category being assigned.
 * @param {object} params.nextDoc The package or contract taking the category.
 * @param {object} [params.currentDoc] The offering holding the category today, if any.
 * @returns {Promise<void>}
 * @throws {Error} CATEGORY_REASSIGNED when someone else moved the category first.
 */
const assignOfferingCategory = async ({ category, nextDoc, currentDoc }) => {
  const db = getFirebase().default.firestore();

  await db.runTransaction(async (transaction) => {
    if (currentDoc && currentDoc.id !== nextDoc.id) {
      const currentSnapshot = await transaction.get(currentDoc.ref);

      if (currentSnapshot.data()?.category !== category) {
        throw new Error(CATEGORY_REASSIGNED);
      }

      transaction.update(currentDoc.ref, { category: null });
    }

    transaction.update(nextDoc.ref, { category });
  });
};

export default assignOfferingCategory;
export { CATEGORY_REASSIGNED };
